/**
 * Scan a clipboard binary dump (Apple Web Archive, RTFD) for PNG/JPEG rasters.
 * Chrome does not expose those flavors as `text/rtf` \pict groups.
 */

const PNG_SIG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIG = [0xff, 0xd8, 0xff];
const PNG_IEND = [0x49, 0x45, 0x4e, 0x44];

function matchesAt(bytes: Uint8Array, index: number, sig: number[]): boolean {
  if (index + sig.length > bytes.length) {
    return false;
  }
  return sig.every((value, offset) => bytes[index + offset] === value);
}

function extractPng(bytes: Uint8Array, start: number): Uint8Array | null {
  let cursor = start + 8;
  while (cursor + 8 <= bytes.length) {
    const length =
      ((bytes[cursor] << 24) |
        (bytes[cursor + 1] << 16) |
        (bytes[cursor + 2] << 8) |
        bytes[cursor + 3]) >>>
      0;
    if (length < 0 || cursor + 12 + length > bytes.length) {
      return null;
    }
    const isIend = matchesAt(bytes, cursor + 4, PNG_IEND);
    cursor += 12 + length;
    if (isIend) {
      return bytes.slice(start, cursor);
    }
  }
  return null;
}

function extractJpeg(bytes: Uint8Array, start: number): Uint8Array | null {
  for (let index = start + 3; index < bytes.length - 1; index += 1) {
    if (bytes[index] === 0xff && bytes[index + 1] === 0xd9) {
      return bytes.slice(start, index + 2);
    }
  }
  return null;
}

export function latin1ToBytes(raw: string): Uint8Array {
  const bytes = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) {
    bytes[index] = raw.charCodeAt(index) & 0xff;
  }
  return bytes;
}

export function extractRasterImagesFromBytes(bytes: Uint8Array): File[] {
  const files: File[] = [];
  let index = 0;
  while (index < bytes.length) {
    if (matchesAt(bytes, index, PNG_SIG)) {
      const png = extractPng(bytes, index);
      if (png && png.length > 24) {
        files.push(
          new File([png], `pasted-clipboard-${files.length}.png`, {
            type: 'image/png',
          }),
        );
        index += png.length;
        continue;
      }
    }
    if (matchesAt(bytes, index, JPEG_SIG)) {
      const jpeg = extractJpeg(bytes, index);
      if (jpeg && jpeg.length > 8) {
        files.push(
          new File([jpeg], `pasted-clipboard-${files.length}.jpg`, {
            type: 'image/jpeg',
          }),
        );
        index += jpeg.length;
        continue;
      }
    }
    index += 1;
  }
  return files;
}

export function extractRasterImagesFromLatin1(raw: string): File[] {
  if (!raw) {
    return [];
  }
  return extractRasterImagesFromBytes(latin1ToBytes(raw));
}
